import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { ActionButton, Pill, AlertBanner } from "@/components/shared";
import { resolvePrimaryAction, sanitizeReason } from "@/lib/aiCommandActions";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

// AI Command Card — "Powered by Allison".
// Presentation-layer composition of the existing governed advisory services:
//   P8-C  POST /staff-assist/case-assist        (Conversation Summary + Next-Step Recommendations)
//   P9-A  POST /staff-assist/tool-recommendations (Recommended internal tools)
// It generates NO AI, persists nothing, executes nothing. Allison advises; staff decide.

const TYPE_LABELS = {
  RequestInformation: "Request Information", PrepareDocuments: "Prepare Documents",
  AwaitClient: "Await Client", InternalReview: "Internal Review",
  ScheduleMeeting: "Schedule Meeting", CompleteFiling: "Complete Filing", Escalate: "Escalate",
};

// Priority → accent identity (High = immediate, Medium = needs review, Low = routine).
const PRIORITY = {
  High:   { border: "border-l-red-500",     chip: "danger",  bar: "bg-red-500",     ring: "ring-red-100",     icon: "AlertCircle",  label: "High Priority" },
  Medium: { border: "border-l-amber-500",   chip: "warning", bar: "bg-amber-500",   ring: "ring-amber-100",   icon: "AlertTriangle", label: "Needs Review" },
  Low:    { border: "border-l-emerald-500", chip: "success", bar: "bg-emerald-500", ring: "ring-emerald-100", label: "Routine",     icon: "CheckCircle2" },
};
const priorityOf = (p) => PRIORITY[p] || { border: "border-l-blue-500", chip: "info", bar: "bg-blue-500", ring: "ring-blue-100", icon: "Info", label: "Normal" };

// Confidence is descriptive (High/Medium/Low) from the advisory services — never a fabricated %.
const CONFIDENCE = {
  High:   { pct: 92, label: "High",   bar: "bg-emerald-500", hint: "Strong supporting context; low uncertainty." },
  Medium: { pct: 60, label: "Medium", bar: "bg-amber-500",   hint: "Reasonable basis; some information may be incomplete." },
  Low:    { pct: 30, label: "Low",    bar: "bg-slate-400",   hint: "Limited context; treat as a starting point." },
};
const confidenceOf = (c) => CONFIDENCE[c] || CONFIDENCE.Medium;

const TOOLS = {
  DraftEmail: { label: "Draft Email", icon: "Mail" },
  ConversationSummary: { label: "Client Summary", icon: "FileText" },
  NextStepRecommendations: { label: "Next Steps", icon: "ListChecks" },
  CaseAssist: { label: "Case Assist", icon: "Sparkles" },
  KnowledgeRetrieval: { label: "Knowledge Lookup", icon: "Library" },
  SchedulingReview: { label: "Scheduler", icon: "CalendarClock" },
};

const genContextId = () =>
  "ctx_" + Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, "0")).join("");

const cleanList = (arr) =>
  (Array.isArray(arr) ? arr : []).map((s) => String(s || "").trim()).filter(Boolean);

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} strokeWidth={2} />;
}

function ConfidenceBar({ level, testid }) {
  const cf = confidenceOf(level);
  return (
    <div data-testid={testid} title={cf.hint}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wide text-slate-400">Confidence</span>
        <span className="text-xs font-semibold text-slate-600">{cf.label}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${cf.bar} transition-all duration-500`} style={{ width: `${cf.pct}%` }} />
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-700 truncate">{value}</div>
    </div>
  );
}

function ToolChip({ name, onClick }) {
  const t = TOOLS[name] || { label: name, icon: "Wrench" };
  return (
    <button
      type="button"
      onClick={() => onClick?.(name)}
      data-testid={`ai-cmd-tool-${name}`}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors"
    >
      <Icon name={t.icon} className="h-3.5 w-3.5" />
      {t.label}
    </button>
  );
}

export default function AICommandCard({
  buildContext,
  workflowId,
  recommendedTeam,
  assignedTeam,
  routing,
  bottleneck,
  variant = "full",
  autoLoad = true,
  onTakeAction,
  onToolClick,
  clientId,
  journeyAction,
  className = "",
}) {
  const [showTrace, setShowTrace] = useState(false);
  const [loading, setLoading] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [pendingDecision, setPendingDecision] = useState(null);
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [error, setError] = useState(null);
  const [ranOnce, setRanOnce] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const autoRef = useRef(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = buildContext ? buildContext() : {};
      const clientId = base.lead_client_id;
      // Single source of truth: is there a Pending AI Decision gating this workflow?
      const pendingP = clientId
        ? api.get(`/case-analysis/pending-decision/client/${clientId}`).then((r) => r.data?.decision || null).catch(() => null)
        : Promise.resolve(null);
      // Reveal the gate immediately — independent of the slower case-assist LLM call.
      pendingP.then(setPendingDecision);
      const conversation_context = {
        context_id: genContextId(),
        conversation_id: base.conversation_id || "unknown",
        ...(base.lead_client_id ? { lead_client_id: base.lead_client_id } : {}),
        ...(base.case_stage ? { case_stage: base.case_stage } : {}),
        client_goals: cleanList(base.client_goals),
        open_questions: cleanList(base.open_questions),
        known_decisions: cleanList(base.known_decisions),
      };
      const caseBody = { conversation_context };
      if (workflowId) caseBody.workflow_id = workflowId;

      const caseP = api.post("/staff-assist/case-assist", caseBody);
      // P9-A also takes { conversation_context } (tools are advisory only).
      const toolsP = api.post("/staff-assist/tool-recommendations", { conversation_context })
        .then((r) => r.data?.recommendations || [])
        .catch(() => []); // tools are non-critical; degrade gracefully

      const [caseRes, toolRes] = await Promise.all([caseP, toolsP]);
      setCaseData(caseRes.data.case_assist);
      setTools(Array.isArray(toolRes) ? toolRes : []);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Allison could not prepare a recommendation right now.");
      setCaseData(null);
      setTools([]);
    } finally {
      setLoading(false);
      setRanOnce(true);
    }
  }, [buildContext, workflowId]);

  useEffect(() => {
    if (autoLoad && !autoRef.current && !loading) {
      autoRef.current = true;
      fetchAll();
    }
  }, [autoLoad, loading, fetchAll]);

  const summary = caseData?.summary || null;
  const recs = caseData?.recommendations || [];
  const gated = pendingDecision?.approval_status === "awaiting_approval";
  const wasRejected = pendingDecision?.approval_status === "rejected";
  const reviewLink = pendingDecision?.review_link;
  const primary = gated ? null : (recs[0] || null);
  const alternatives = recs.slice(1, showAll ? recs.length : 4);
  const toolNames = tools.map((t) => t.tool_name).filter(Boolean);

  // Governed: the primary action's label + destination come from the canonical Journey Evaluator
  // stage (with case-assist recommendation-type refinement) — never a generic destination.
  const primaryAction = primary ? resolvePrimaryAction({ primary, journeyAction, clientId }) : null;

  // Reason bullets: the recommendation explanation, plus a couple of grounding facts from the
  // summary. Sanitized (firm-capability safeguard) and de-duplicated.
  const reasonBullets = primary
    ? Array.from(new Set(
        [primary.explanation, ...cleanList(summary?.key_facts).slice(0, 3)]
          .filter(Boolean)
          .map(sanitizeReason)
      )).slice(0, 4)
    : [];

  const condensed = variant === "condensed";

  // ---- states ----
  const Shell = ({ children }) => (
    <div
      data-testid="ai-command-card"
      className={`relative rounded-2xl border border-slate-200 border-l-4 ${gated ? "border-l-amber-500" : primary ? priorityOf(primary.priority).border : "border-l-blue-500"} bg-white shadow-sm ${condensed ? "p-4" : "p-5"} ${className}`}
    >
      {children}
    </div>
  );

  const Header = () => (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-sm shrink-0">
          <Icon name="Sparkles" className="h-[18px] w-[18px] text-white" />
        </span>
        <div className="min-w-0">
          <div className="font-heading font-bold text-slate-900 leading-tight text-[15px] tracking-tight">AI Command Card</div>
          <div className="text-[11px] text-slate-400 leading-tight">Powered by Allison</div>
        </div>
      </div>
      <button
        onClick={fetchAll} disabled={loading}
        data-testid="ai-cmd-refresh"
        className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-50 transition-colors"
        title="Re-run Allison"
      >
        <Icons.RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );

  return (
    <>
      <Shell>
        <Header />

        {bottleneck && <BottleneckStrip bottleneck={bottleneck} navigate={navigate} condensed={condensed} />}

        {loading && !gated && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center" data-testid="ai-cmd-loading">
            <Icons.Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            Allison is analyzing this case…
          </div>
        )}

        {/* HARD WORKFLOW GATE — a Pending AI Decision must be resolved before any downstream action. */}
        {gated && (
          <div data-testid="ai-cmd-gate">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 rounded px-2 py-0.5" data-testid="ai-cmd-gate-badge">
                  <Icons.AlertTriangle className="h-3.5 w-3.5" />Action Required
                </span>
              </div>
              <Pill tone={priorityOf(pendingDecision.priority).chip}>
                <Icon name={priorityOf(pendingDecision.priority).icon} className="h-3 w-3 mr-1" />
                {priorityOf(pendingDecision.priority).label}
              </Pill>
            </div>
            <h3 className={`font-heading font-semibold text-slate-900 ${condensed ? "text-base" : "text-lg"} leading-snug mt-2`} data-testid="ai-cmd-primary-title">
              Review {pendingDecision.decision_type_label}
            </h3>
            <p className="text-sm text-slate-600 mt-1">Allison finished the analysis. The workflow is blocked until you approve or reject it.</p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              {pendingDecision.confidence ? <Meta label="Confidence" value={`${pendingDecision.confidence}%`} /> : null}
              <Meta label="Priority" value={pendingDecision.priority} />
              <Meta label="Services" value={pendingDecision.service_count} />
              {pendingDecision.meeting_date ? <Meta label="Meeting" value={new Date(pendingDecision.meeting_date).toLocaleDateString()} /> : null}
            </div>
            <div className="mt-4">
              <button onClick={() => navigate(reviewLink)} data-testid="ai-cmd-review-approve"
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm font-semibold shadow-sm transition-colors">
                <Icons.Sparkles className="h-4 w-4" />Review &amp; Approve
              </button>
            </div>
            {recs.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100 opacity-60 pointer-events-none select-none" data-testid="ai-cmd-locked-recs">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Icons.Lock className="h-3 w-3" />Available after approval</div>
                <ul className="space-y-1.5">
                  {recs.slice(0, 3).map((r, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                      <Icons.ChevronRight className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{TYPE_LABELS[r.recommendation_type] || r.recommendation_type}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!loading && !gated && error && (
          <div data-testid="ai-cmd-error" className="py-4">
            <AlertBanner tone="warning">{error}</AlertBanner>
            <div className="mt-3">
              <ActionButton icon="RefreshCw" variant="secondary" onClick={fetchAll} data-testid="ai-cmd-retry">Try again</ActionButton>
            </div>
          </div>
        )}

        {!loading && !gated && !error && ranOnce && !primary && (
          <div className="py-4" data-testid="ai-cmd-empty">
            <div className="text-sm text-slate-500">
              {wasRejected ? "AI analysis rejected — no next action promoted. Choose the next action manually." : "Allison has no specific recommendation from the current context yet."}
            </div>
            {wasRejected && reviewLink && (
              <button onClick={() => navigate(reviewLink)} data-testid="ai-cmd-reanalyze"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 px-2.5 py-1.5 text-xs font-medium transition-colors">
                <Icons.Sparkles className="h-3.5 w-3.5" />Ask Allison to re-analyze
              </button>
            )}
          </div>
        )}

        {!loading && !gated && !error && primary && (
          <div data-testid="ai-cmd-content">
            {wasRejected && (
              <div className="mb-3 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5" data-testid="ai-cmd-rejected-notice">
                <div className="flex items-center gap-2 text-xs text-rose-700">
                  <Icons.XCircle className="h-3.5 w-3.5 shrink-0" />
                  AI analysis rejected — Allison did not promote a next step. Choose the next action manually.
                </div>
                {reviewLink && (
                  <button onClick={() => navigate(reviewLink)} data-testid="ai-cmd-reanalyze"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-white border border-rose-200 text-rose-700 hover:bg-rose-100 px-2.5 py-1.5 text-xs font-medium transition-colors">
                    <Icons.Sparkles className="h-3.5 w-3.5" />Ask Allison to re-analyze
                  </button>
                )}
              </div>
            )}
            {/* Primary recommendation */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h3 className={`font-heading font-semibold text-slate-900 ${condensed ? "text-base" : "text-lg"} leading-snug`} data-testid="ai-cmd-primary-title">
                {TYPE_LABELS[primary.recommendation_type] || primary.recommendation_type}
              </h3>
              <Pill tone={priorityOf(primary.priority).chip} data-testid="ai-cmd-priority">
                <Icon name={priorityOf(primary.priority).icon} className="h-3 w-3 mr-1" />
                {priorityOf(primary.priority).label}
              </Pill>
            </div>

            <div className="mt-3">
              <ConfidenceBar level={primary.confidence} testid="ai-cmd-confidence" />
            </div>

            {/* Reason */}
            {reasonBullets.length > 0 && (
              <div className="mt-3" data-testid="ai-cmd-reason">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">
                  Allison recommends this because
                </div>
                <ul className="space-y-1">
                  {reasonBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Icons.Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-500" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Routing recommendation — shown when Allison's engagement-routing engine suggests a
                different primary owner, collaboration, or Management review. Advisory only. */}
            {(() => {
              if (condensed || !routing) return null;
              const primary = routing.suggested_team || routing.recommended_team;
              const collab = routing.recommended_collaboration || [];
              const notAccepted = routing.accepted === false;
              const differs = primary && primary !== assignedTeam;
              const show = notAccepted || differs || collab.length > 0 || routing.requires_management_review;
              if (!show) return null;
              const cls = notAccepted
                ? { border: "border-rose-200", bg: "bg-rose-50", label: "text-rose-600", arrow: "text-rose-500" }
                : { border: "border-amber-200", bg: "bg-amber-50", label: "text-amber-600", arrow: "text-amber-500" };
              return (
                <div className={`mt-4 rounded-lg border ${cls.border} ${cls.bg} px-3 py-2.5`} data-testid="ai-cmd-routing">
                  <div className="flex items-center justify-between">
                    <div className={`text-[11px] uppercase tracking-wide ${cls.label}`}>Routing Recommendation</div>
                    {routing.confidence && <span className="text-[10px] font-semibold text-slate-500">{routing.confidence} confidence</span>}
                  </div>
                  {notAccepted ? (
                    <div className="mt-1 text-sm font-semibold text-rose-800" data-testid="ai-cmd-routing-primary">Confirm engagement fit</div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm mt-1">
                      <span className="text-slate-500 line-through decoration-slate-300">{assignedTeam || "Unassigned"}</span>
                      <Icons.ArrowRight className={`h-3.5 w-3.5 ${cls.arrow} shrink-0`} />
                      <span className="font-semibold text-slate-800" data-testid="ai-cmd-routing-primary">{primary}</span>
                    </div>
                  )}
                  {collab.length > 0 && (
                    <div className="mt-1 text-xs text-slate-600" data-testid="ai-cmd-routing-collab">Collaborate: <span className="font-medium">{collab.join(", ")}</span></div>
                  )}
                  {routing.requires_management_review && (
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700" data-testid="ai-cmd-routing-mgmt"><Icons.ShieldAlert className="h-3.5 w-3.5" />Management review recommended</div>
                  )}
                  {routing.reason && <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{routing.reason}</p>}
                  {(routing.routing_trace || []).length > 0 && (
                    <>
                      <button onClick={() => setShowTrace((s) => !s)} data-testid="ai-cmd-routing-trace-toggle"
                        className="mt-1.5 text-[11px] font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
                        {showTrace ? "Hide" : "Show"} routing trace
                        <Icons.ChevronDown className={`h-3 w-3 transition-transform ${showTrace ? "rotate-180" : ""}`} />
                      </button>
                      {showTrace && (
                        <ul className="mt-1 space-y-0.5 text-[11px] text-slate-500" data-testid="ai-cmd-routing-trace">
                          {routing.routing_trace.map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* Actions — primary launches the workflow that performs Allison's recommendation */}
            <div className="mt-4" data-testid="ai-cmd-actions">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Recommended Next Action</div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onTakeAction?.(primaryAction || primary)}
                  data-testid="ai-cmd-take-action"
                  title={primaryAction?.reason || undefined}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
                >
                  <Icon name={primaryAction?.icon || "Zap"} className="h-4 w-4" /> {primaryAction?.label || "Take Action"}
                </button>
                <button
                  onClick={() => setBriefingOpen(true)}
                  data-testid="ai-cmd-view-briefing"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 text-sm font-medium transition-colors"
                >
                  <Icons.PanelRightOpen className="h-4 w-4" /> View AI Briefing
                </button>
              </div>
            </div>

            {/* Alternatives — each is a real, launchable next action (from the same evaluator) */}
            {alternatives.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100" data-testid="ai-cmd-alternatives">
                <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Alternative Recommendations</div>
                <ul className="space-y-1">
                  {alternatives.map((r, i) => {
                    const alt = resolvePrimaryAction({ primary: r, clientId });
                    return (
                      <li key={r.recommendation_id || i}>
                        <button
                          onClick={() => onTakeAction?.(alt)}
                          data-testid={`ai-cmd-alt-${i}`}
                          title={r.explanation || undefined}
                          className="w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 text-left transition-colors"
                        >
                          <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
                            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${priorityOf(r.priority).bar}`} />
                            <Icon name={alt.icon} className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">{alt.label}</span>
                          </span>
                          <Icons.ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {recs.length > 4 && (
                  <button onClick={() => setShowAll((s) => !s)} data-testid="ai-cmd-show-more"
                    className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                    {showAll ? "Show less" : `Show more (${recs.length - 4})`}
                    <Icons.ChevronDown className={`h-3 w-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </Shell>

      {/* AI Briefing drawer — composes already-fetched P8 services (nothing new generated) */}
      <Sheet open={briefingOpen} onOpenChange={setBriefingOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto tf-scroll" data-testid="ai-briefing-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Icon name="Sparkles" className="h-4 w-4 text-blue-600" /> AI Briefing
            </SheetTitle>
          </SheetHeader>
          <SheetDescription className="sr-only">Allison's advisory briefing for this case — summary, recommended actions, and tools.</SheetDescription>
          <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-4">
            Composed from Allison's advisory services · advisory only
          </p>

          <div className="space-y-6">
            {summary && (
              <BriefingSection icon="FileText" title="Executive Summary">
                <p className="text-sm text-slate-700 leading-relaxed">{summary.executive_summary}</p>
                {summary.current_client_objective && (
                  <div className="mt-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Current Client Objective</div>
                    <p className="text-sm text-slate-700">{summary.current_client_objective}</p>
                  </div>
                )}
                <BriefingList label="Key Facts" items={summary.key_facts} />
              </BriefingSection>
            )}

            {recs.length > 0 && (
              <BriefingSection icon="ArrowRightCircle" title="Recommended Actions">
                <ol className="space-y-2.5">
                  {recs.map((r, i) => (
                    <li key={r.recommendation_id || i} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-medium text-slate-800">{TYPE_LABELS[r.recommendation_type] || r.recommendation_type}</span>
                        <Pill tone={priorityOf(r.priority).chip}>{r.priority}</Pill>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{r.explanation}</p>
                    </li>
                  ))}
                </ol>
              </BriefingSection>
            )}

            {toolNames.length > 0 && (
              <BriefingSection icon="Wrench" title="Suggested Tools">
                <div className="flex flex-wrap gap-1.5">
                  {toolNames.map((t) => <ToolChip key={t} name={t} onClick={onToolClick} />)}
                </div>
              </BriefingSection>
            )}

            {cleanList(summary?.risks).length > 0 && (
              <BriefingSection icon="ShieldAlert" title="Risks / Watch Items">
                <BriefingList items={summary.risks} />
              </BriefingSection>
            )}

            {cleanList(summary?.outstanding_items).length > 0 && (
              <BriefingSection icon="CircleHelp" title="Outstanding Information">
                <BriefingList items={summary.outstanding_items} />
              </BriefingSection>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

const BOTTLENECK_SEV = {
  High:   { wrap: "bg-rose-50 border-rose-200", label: "text-rose-900", icon: "text-rose-500" },
  Medium: { wrap: "bg-amber-50 border-amber-200", label: "text-amber-900", icon: "text-amber-500" },
  Low:    { wrap: "bg-emerald-50 border-emerald-200", label: "text-emerald-900", icon: "text-emerald-500" },
};

function BottleneckStrip({ bottleneck, navigate, condensed }) {
  const sev = BOTTLENECK_SEV[bottleneck.severity] || BOTTLENECK_SEV.Medium;
  const isClient = bottleneck.owner === "client";
  return (
    <div data-testid="ai-cmd-bottleneck" className={`mb-3 rounded-xl border px-3.5 py-3 ${sev.wrap}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon name={isClient ? "UserRound" : "UserCog"} className={`h-4 w-4 mt-0.5 shrink-0 ${sev.icon}`} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Workflow is waiting on</div>
            <div className={`font-heading font-semibold text-sm leading-snug ${sev.label}`} data-testid="ai-cmd-bottleneck-label">
              {bottleneck.label}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">{bottleneck.detail}</div>
          </div>
        </div>
        <span data-testid="ai-cmd-bottleneck-owner"
          className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-white/70 border border-white px-2 py-0.5 text-slate-600 shrink-0">
          {isClient ? "On Client" : "On Staff"}
        </span>
      </div>
      {bottleneck.cta_link && (
        <button onClick={() => navigate(bottleneck.cta_link)} data-testid="ai-cmd-bottleneck-cta"
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-[#171B2E] hover:bg-[#222842] text-white px-3 py-1.5 text-xs font-semibold transition-colors">
          <Icons.ArrowRight className="h-3.5 w-3.5" />{bottleneck.cta_label}
        </button>
      )}
      {bottleneck.allison && <AllisonChip allison={bottleneck.allison} navigate={navigate} />}
    </div>
  );
}

function AllisonChip({ allison, navigate }) {
  const sc = allison.severity_counts || {};
  const high = (sc.critical || 0) + (sc.high || 0);
  let body;
  if (allison.status === "running") {
    body = <span className="inline-flex items-center gap-1.5 text-violet-700"><Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Allison reviewing organizer…</span>;
  } else if (allison.status === "failed") {
    body = <span className="text-rose-600">Allison review failed — retry available</span>;
  } else if (allison.all_resolved) {
    body = <span className="text-emerald-700">Allison review complete · all findings resolved</span>;
  } else {
    body = <span className="text-violet-800 font-medium">Allison found {allison.findings_total} item{allison.findings_total === 1 ? "" : "s"}{high > 0 ? ` · ${high} high/critical` : ""}</span>;
  }
  return (
    <button onClick={() => navigate(allison.cta_link)} data-testid="ai-cmd-allison-chip"
      className="mt-2.5 w-full flex items-center justify-between gap-2 rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2 text-xs hover:bg-violet-50 transition-colors">
      <span className="inline-flex items-center gap-1.5 min-w-0"><Icons.Brain className="h-3.5 w-3.5 text-violet-500 shrink-0" />{body}</span>
      {allison.status === "completed" && !allison.all_resolved && <span className="text-violet-600 font-semibold shrink-0">Review Now →</span>}
      {allison.status === "failed" && <span className="text-rose-600 font-semibold shrink-0">Retry →</span>}
    </button>
  );
}

function BriefingSection({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={icon} className="h-4 w-4 text-slate-500" />
        <h4 className="font-heading text-sm font-semibold text-slate-800">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function BriefingList({ label, items }) {
  const list = cleanList(items);
  if (list.length === 0) return null;
  return (
    <div className={label ? "mt-3" : ""}>
      {label && <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>}
      <ul className="space-y-1">
        {list.map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
            <Icons.Dot className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" /><span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
